import { ArrowBack, NavigateNext } from '@mui/icons-material';
import { Box, Breadcrumbs, IconButton, Typography } from '@mui/material';
import { useMemo } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { t } from '../../../constants/translations';
import { useAdmin } from '../../../contexts/admin.context';
import { useCafe } from '../../../contexts/cafe.context';
import { useLocale } from '../../../contexts/locale.context';
import useMedia from '../../../utils/useMedia';

type Params = {
  sectionId: string;
  categoryId: string;
};

const MenuBreadcrumbs = () => {
  const { sectionId, categoryId } = useParams<Params>();
  const { sections, categories } = useCafe();
  const { cafeSlug } = useAdmin();
  const { lang } = useLocale();
  const { isMobile } = useMedia();
  const navigate = useNavigate();
  const section = useMemo(() => sections.find((s) => s.id === sectionId), [sectionId, sections]);
  const category = useMemo(() => categories.find((c) => c.id === categoryId), [categories, categoryId]);

  const breadcrumbs = useMemo(() => {
    const breadcrumbs = [{ to: `/${cafeSlug}/menu`, label: t.menu_sections[lang] }];

    if (section?.name) {
      breadcrumbs.push({ to: `/${cafeSlug}/menu/${sectionId}`, label: section.name });
    }

    if (category?.name) {
      breadcrumbs.push({ to: `/${cafeSlug}/menu/${sectionId}`, label: category.name });
    }

    return breadcrumbs;
  }, [cafeSlug, category?.name, lang, section?.name, sectionId]);

  return (
    <Box sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
      <Box sx={{ width: 40, height: 40, mr: isMobile ? 1 : 2 }}>
        {breadcrumbs.length > 1 && (
          <IconButton onClick={() => navigate(breadcrumbs[breadcrumbs.length - 2]?.to || '')}>
            <ArrowBack />
          </IconButton>
        )}
      </Box>
      <Breadcrumbs separator={<NavigateNext fontSize="small" />}>
        {breadcrumbs.map((bc, index) => {
          if (index === breadcrumbs.length - 1) {
            return (
              <Typography variant={isMobile ? 'body1' : 'h6'} key={bc.to}>
                {bc.label}
              </Typography>
            );
          }

          return (
            <Typography
              variant={isMobile ? 'body1' : 'h6'}
              key={bc.to}
              sx={{ a: { cursor: 'pointer', textDecoration: 'none', color: (t) => t.palette.text.primary } }}
            >
              <RouterLink to={bc.to}>{bc.label}</RouterLink>
            </Typography>
          );
        })}
      </Breadcrumbs>
    </Box>
  );
};

export default MenuBreadcrumbs;
